import { Button, Space, Typography, Popconfirm } from "antd";
import ListTable from "common/ListTable";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { cglFormatDate, hasPermission } from "utils/functions";
import { getData, deleteData } from "../../../../apiServices/common";
import AppContent from "../../../../common/AppContent";
import HandleNotification from "../../../../common/Notification";
import AppPageHeader from "../../../../common/PageHeader";
import ExtraContent from "../../../Configuration/CommonComponents/ExtraContent";
import { IMPORT_MACHINE_INFO } from "apiServices/API_ENDPOINTS";
import { MACHINE_INFORMATION_IMPORT_PERMISSION } from "routes/permissions";

const MachineInformation = (props) => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const navigate = useNavigate();
  const { id } = useParams();

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState();

  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [filterValues, setFilterValues] = useState({});
  const [error, setError] = useState(null);

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const onFilterChange = (values) => {
    setFilterValues(values);
  };

  const fetchData = useCallback(
    async (filterValues) => {
      setError(null);
      const query = `/api/digitalization/machine-maintenance/machine-information`;
      const bodyData = {
        page: page,
        per_page: filterValues?.per_page || 10,
        ...(filterValues?.machine_identifier && {
          machine_identifier: filterValues?.machine_identifier,
        }),
        ...(filterValues?.machine_name && {
          machine_name: filterValues?.machine_name,
        }),
        ...(filterValues?.machine_action_type_id && {
          machine_action_type_id: filterValues?.machine_action_type_id,
        }),
        ...(filterValues?.machine_brand_id && {
          machine_brand_id: filterValues?.machine_brand_id,
        }),
        ...(filterValues?.machine_model_id && {
          machine_model_id: filterValues?.machine_model_id,
        }),
        ...(filterValues?.machine_location_id && {
          machine_location_id: filterValues?.machine_location_id,
        }),
        ...(filterValues?.machine_line_id && {
          machine_line_id: filterValues?.machine_line_id,
        }),
        ...(filterValues?.machine_origin_country_id && {
          machine_origin_country_id: filterValues?.machine_origin_country_id,
        }),
        ...(filterValues?.sewing_line_id && {
          sewing_line_id: filterValues?.sewing_line_id,
        }),
      };

      const response = await getData(query, false, bodyData);

      if (response && response?.data?.code === 200) {
        setTableData(response?.data?.data?.data);
        setPageSize(response?.data?.data?.per_page);
        setTotal(response?.data?.data?.total);
      } else {
        HandleNotification("error", "Error", "Something went wrong");
        setError("Could not load this list.");
      }
    },
    [page],
  );

  useEffect(() => {
    const value = {
      ...filterValues,
      page: page,
    };
    fetchData(value);
  }, [refresh, page, pageSize, filterValues, fetchData]);

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const handleEdit = (event, record) => {
    const url = `/digitalization/configurations/machine-information/${event}/${record.id}`;
    navigate(url);
  };

  const handleDelete = async (record) => {
    try {
      const response = await deleteData(
        `/api/digitalization/machine-maintenance/machine-information/${record.id}`,
      );

      if (response?.code === 200) {
        HandleNotification(
          "success",
          "Deleted",
          "Machine information deleted successfully",
        );
        setRefresh((prev) => prev + 1);
      }
    } catch (error) {
      HandleNotification(
        "error",
        "Error",
        "Failed to delete machine information",
      );
    }
  };

  const tableColumns = [
    {
      title: "Machine ID",
      dataIndex: "machine_identifier",
      key: "machine_identifier",
      width: "120px",
    },
    {
      title: "Machine Name",
      dataIndex: "machine_name",
      key: "machine_name",
      width: "150px",
    },
    {
      title: "Action Type",
      dataIndex: ["machine_action_type", "name"],
      key: "machine_action_type",
      width: "150px",
    },
    {
      title: "Brand",
      dataIndex: ["machine_brand", "name"],
      key: "machine_brand",
      width: "120px",
    },
    {
      title: "Location",
      dataIndex: ["machine_location", "name"],
      key: "machine_location",
      width: "120px",
    },
    {
      title: "Model",
      dataIndex: ["machine_model", "name"],
      key: "machine_model",
      width: "120px",
    },
    {
      title: "Origin Country",
      dataIndex: ["machine_origin_country", "name"],
      key: "machine_origin_country",
      width: "120px",
    },
    {
      title: "Sewing Line",
      dataIndex: ["sewing_line", "name"],
      key: "sewing_line",
      width: "120px",
      render: (_, record) =>
        record.sewing_line?.name || record.machine_line?.name || "N/A",
    },
    {
      title: "Invoice No",
      dataIndex: "invoice_no",
      key: "invoice_no",
      width: "120px",
    },
    {
      title: "Invoice Year",
      dataIndex: "invoice_year",
      key: "invoice_year",
      width: "100px",
    },
    {
      title: "Ownership Type",
      dataIndex: "ownership_type",
      key: "ownership_type",
      width: "120px",
    },
    {
      title: "Installation Year",
      dataIndex: "installation_year",
      key: "installation_year",
      width: "120px",
    },
    {
      title: "Status",
      dataIndex: "status_label",
      key: "status_label",
      width: "100px",
    },
    {
      title: "Created By",
      dataIndex: ["created_by", "name"],
      key: "created_by",
      width: "150px",
      render: (_, record) => record.created_by?.name || "N/A",
    },
    {
      title: "Action",
      key: "action",
      width: "100px",
      render: (_, record) => (
        <Space size="small">
          <Button
            style={{ marginLeft: 5 }}
            size="small"
            onClick={(event) => {
              handleEdit("edit", record);
            }}
          >
            Edit
          </Button>
          <Button
            style={{ marginLeft: 5 }}
            size="small"
            onClick={(event) => {
              handleEdit("view", record);
            }}
          >
            View
          </Button>
          <Popconfirm
            title="Are you sure to delete this machine information?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button style={{ marginLeft: 5 }} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={onFilterChange}
        filterName={"machine-information"}
        setShowFilter={setShowFilter}
        showFilter={showFilter}
      />
    ),
    [showFilter],
  );

  const addOrUpdateRoute = () => {
    const route = id
      ? `/digitalization/configurations/machine-information/edit/${id}`
      : `/digitalization/configurations/machine-information/store`;
    navigate(route);
  };

  const header = (
    <AppPageHeader
      extra={
        <ExtraContent
          refresh={refresh}
          setRefresh={setRefresh}
          handleAddItemOpen={addOrUpdateRoute}
          setShowFilter={setShowFilter}
          addButtonText={
            hasPermission([MACHINE_INFORMATION_IMPORT_PERMISSION])
              ? "Add New"
              : ""
          }
          showFilter={showFilter}
          hasImportButton={
            hasPermission([MACHINE_INFORMATION_IMPORT_PERMISSION])
              ? true
              : false
          }
          uploadAPI={IMPORT_MACHINE_INFO}
          acceptedFileTypes={".xlsx"}
        />
      }
      title={<Title level={5}>Machine Information</Title>}
    />
  );
  const content = (
    <>
      {memoizedCustomFilter}
      <ListTable
        columns={tableColumns}
        dataSource={tableData}
        loading={!tableData}
        error={error}
        onRetry={() => fetchData({ ...filterValues, page })}
        emptyText="No records to show yet."
        pagination={{
          showSizeChanger: false,
          total: total,
          current: page,
          pageSize: pageSize,
          onChange: onPageChange,
        }}
        scroll={{ x: "max-content" }}
      />
    </>
  );
  return (
    <>
      <AppContent header={header} content={content} />
    </>
  );
};

export default MachineInformation;
